import { useState, useEffect } from 'react';
import { salarySlipApi, teacherApi } from '../lib/api';
import { usePermissions } from '../components/Layout';
import {
  Plus,
  Search,
  Trash2,
  Edit,
  Download,
  Check,
  Clock,
  Users,
  DollarSign,
  Loader2,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const POSITIONS = [
  'Enseignant Coranique',
  'Enseignant Franco-Arabe',
  'Directeur',
  'Surveillant',
  'Secrétaire',
  'Comptable',
  'Agent d\'entretien',
  'Cuisinier',
  'Gardien',
  'Autre',
];

export default function SalarySlipsPage() {
  const { hasPermission } = usePermissions();
  const [slips, setSlips] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [editingSlip, setEditingSlip] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const currentMonthStr = `${selectedYear}-${selectedMonth}`;

  const [newSlip, setNewSlip] = useState({
    employee_id: '',
    employee_type: 'teacher',
    employee_name: '',
    position: 'Enseignant Coranique',
    month: currentMonthStr,
    base_salary: 0,
    bonus: 0,
    deductions: 0,
    net_salary: 0,
    payment_mode: 'cash',
    notes: '',
    status: 'pending',
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const monthFilter = `${selectedYear}-${selectedMonth}`;
      const params = { month: monthFilter };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const [slipsRes, teachersRes, statsRes] = await Promise.all([
        salarySlipApi.getAll(params),
        teacherApi.getAll({}),
        salarySlipApi.getStats(monthFilter),
      ]);

      setSlips(slipsRes.data);
      setTeachers(teachersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, filterStatus]);

  useEffect(() => {
    // Auto-calculate net salary
    const net = (newSlip.base_salary || 0) + (newSlip.bonus || 0) - (newSlip.deductions || 0);
    setNewSlip((prev) => ({ ...prev, net_salary: net }));
  }, [newSlip.base_salary, newSlip.bonus, newSlip.deductions]);

  const handleTeacherSelect = (teacherId) => {
    const teacher = teachers.find((t) => t.id === teacherId);
    if (teacher) {
      setNewSlip({
        ...newSlip,
        employee_id: teacherId,
        employee_name: `${teacher.first_name} ${teacher.last_name}`,
        position: teacher.teacher_type === 'quranic' ? 'Enseignant Coranique' : 'Enseignant Franco-Arabe',
      });
    }
  };

  const handleAddSlip = async (e) => {
    e.preventDefault();
    if (!newSlip.employee_name || !newSlip.base_salary) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setFormLoading(true);
    try {
      const slipData = {
        ...newSlip,
        month: `${selectedYear}-${selectedMonth}`,
      };

      if (editingSlip) {
        await salarySlipApi.update(editingSlip.id, slipData);
        toast.success('Bulletin mis à jour');
      } else {
        await salarySlipApi.create(slipData);
        toast.success('Bulletin créé');
      }
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await salarySlipApi.markPaid(id);
      toast.success('Bulletin marqué comme payé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bulletin ?')) return;
    try {
      await salarySlipApi.delete(id);
      toast.success('Bulletin supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = async (slipId) => {
    setDownloadingId(slipId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salary-slips/${slipId}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error('Erreur téléchargement');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin_${slipId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEdit = (slip) => {
    setEditingSlip(slip);
    setNewSlip({
      employee_id: slip.employee_id,
      employee_type: slip.employee_type || 'teacher',
      employee_name: slip.employee_name,
      position: slip.position,
      month: slip.month,
      base_salary: slip.base_salary,
      bonus: slip.bonus || 0,
      deductions: slip.deductions || 0,
      net_salary: slip.net_salary,
      payment_mode: slip.payment_mode || 'cash',
      notes: slip.notes || '',
      status: slip.status,
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setEditingSlip(null);
    setNewSlip({
      employee_id: '',
      employee_type: 'teacher',
      employee_name: '',
      position: 'Enseignant Coranique',
      month: currentMonthStr,
      base_salary: 0,
      bonus: 0,
      deductions: 0,
      net_salary: 0,
      payment_mode: 'cash',
      notes: '',
      status: 'pending',
    });
  };

  const filteredSlips = slips.filter((s) =>
    s.employee_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthName = MONTHS.find((m) => m.value === month)?.label || month;
    return `${monthName} ${year}`;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bulletins de Salaire</h1>
          <p className="text-muted-foreground">Gestion des salaires du personnel</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-salary-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau bulletin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSlip ? 'Modifier le bulletin' : 'Nouveau bulletin de salaire'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSlip} className="space-y-4">
              {/* Employee Selection */}
              <div className="space-y-2">
                <Label>Sélectionner un enseignant (optionnel)</Label>
                <Select value={newSlip.employee_id} onValueChange={handleTeacherSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un enseignant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom complet *</Label>
                  <Input
                    value={newSlip.employee_name}
                    onChange={(e) => setNewSlip({ ...newSlip, employee_name: e.target.value })}
                    placeholder="Nom de l'employé"
                    data-testid="salary-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Poste *</Label>
                  <Select value={newSlip.position} onValueChange={(v) => setNewSlip({ ...newSlip, position: v })}>
                    <SelectTrigger data-testid="salary-position">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Salary Details */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Salaire de base *</Label>
                    <Input
                      type="number"
                      value={newSlip.base_salary || ''}
                      onChange={(e) => setNewSlip({ ...newSlip, base_salary: parseFloat(e.target.value) || 0 })}
                      placeholder="100000"
                      data-testid="salary-base"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-green-600">+ Primes</Label>
                    <Input
                      type="number"
                      value={newSlip.bonus || ''}
                      onChange={(e) => setNewSlip({ ...newSlip, bonus: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      data-testid="salary-bonus"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-red-600">- Retenues</Label>
                    <Input
                      type="number"
                      value={newSlip.deductions || ''}
                      onChange={(e) => setNewSlip({ ...newSlip, deductions: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      data-testid="salary-deductions"
                    />
                  </div>
                </div>
                <div className="pt-2 border-t flex justify-between items-center">
                  <span className="font-medium">Net à payer</span>
                  <span className="text-xl font-bold text-primary" data-testid="salary-net">{formatCurrency(newSlip.net_salary)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mode de paiement</Label>
                <Select value={newSlip.payment_mode} onValueChange={(v) => setNewSlip({ ...newSlip, payment_mode: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Input
                  value={newSlip.notes}
                  onChange={(e) => setNewSlip({ ...newSlip, notes: e.target.value })}
                  placeholder="Remarques..."
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} className="flex-1" data-testid="salary-submit-btn">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSlip ? 'Modifier' : 'Créer')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Month Selector */}
      <div className="flex flex-wrap gap-4">
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="paid">Payés</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Employés</p>
                  <p className="text-xl font-bold" data-testid="stat-employees">{stats.total_employees}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Masse salariale</p>
                  <p className="text-xl font-bold" data-testid="stat-masse-salariale">{formatCurrency(stats.total_net_salary)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payés</p>
                  <p className="text-xl font-bold text-green-600" data-testid="stat-paid">{formatCurrency(stats.total_paid)}</p>
                  <p className="text-xs text-muted-foreground">{stats.paid_count} bulletins</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En attente</p>
                  <p className="text-xl font-bold text-orange-600" data-testid="stat-pending">{formatCurrency(stats.total_pending)}</p>
                  <p className="text-xs text-muted-foreground">{stats.pending_count} bulletins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Rechercher un employé..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Bulletins - {getMonthLabel(currentMonthStr)} ({filteredSlips.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredSlips.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Aucun bulletin pour ce mois</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="text-right">Primes</TableHead>
                  <TableHead className="text-right">Retenues</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSlips.map((slip) => (
                  <TableRow key={slip.id} data-testid={`salary-row-${slip.id}`}>
                    <TableCell className="font-medium">{slip.employee_name}</TableCell>
                    <TableCell className="text-muted-foreground">{slip.position}</TableCell>
                    <TableCell className="text-right">{formatCurrency(slip.base_salary)}</TableCell>
                    <TableCell className="text-right text-green-600">+{formatCurrency(slip.bonus || 0)}</TableCell>
                    <TableCell className="text-right text-red-600">{slip.deductions > 0 ? `-${formatCurrency(slip.deductions)}` : '0 F'}</TableCell>
                    <TableCell className="text-right font-bold text-primary">{formatCurrency(slip.net_salary)}</TableCell>
                    <TableCell className="text-center" data-testid={`salary-status-${slip.id}`}>
                      {slip.status === 'paid' ? (
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          <Check className="w-3 h-3 mr-1" /> Payé
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                          <Clock className="w-3 h-3 mr-1" /> En attente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        {slip.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(slip.id)} className="text-green-600" title="Marquer payé" data-testid={`salary-markpaid-${slip.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(slip.id)}
                          disabled={downloadingId === slip.id}
                          title="Télécharger PDF"
                          data-testid={`salary-pdf-${slip.id}`}
                        >
                          {downloadingId === slip.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(slip)} title="Modifier">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(slip.id)} className="text-red-600" title="Supprimer" data-testid={`salary-delete-${slip.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
